import React, { useState, useMemo } from 'react';
import ContactForm from '../dialogs/SupportContactForm';
import '../../styles/LandingPage.scss';
import laptopImage from '../../assets/laptop.png';
import translations from '../../translation.json';

interface LandingPageProps {
    onNavigateToLogin: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigateToLogin }) => {
    const [lang, setLang] = useState<string>(() => {
        try { return (localStorage.getItem('xm_lang') as string) || 'en'; } catch { return 'en'; }
    });
    // Contact form toggle state
    const [showContactForm, setShowContactForm] = useState(false);

    const t = useMemo(() => {
        return (key: string, fallback?: string) => {
            const dict: any = (translations as any)[lang] ?? (translations as any).en;
            return dict[key] ?? fallback ?? key;
        };
    }, [lang]);

    const handleChangeLang = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const v = e.target.value;
        setLang(v);
        try { localStorage.setItem('xm_lang', v); } catch {}
    };

    const changeLang = (v: string) => {
        setLang(v);
        try { localStorage.setItem('xm_lang', v); } catch {}
    };

    const features = (translations as any)[lang]?.features ?? (translations as any).en.features;

    const benefits = (translations as any)[lang]?.benefits ?? (translations as any).en.benefits;

    return (
        <div className="landing-page">
            {/* Language Selector Header */}
            <div className="landing-lang-header">
                <div className="lang-buttons">
                    <button onClick={() => changeLang('en')} className={`lang-button ${lang === 'en' ? 'active' : ''}`} aria-label="English">
                        <span className="lang-flag">🇬🇧</span> EN
                    </button>
                    <button onClick={() => changeLang('de')} className={`lang-button ${lang === 'de' ? 'active' : ''}`} aria-label="Deutsch">
                        <span className="lang-flag">🇩🇪</span> DE
                    </button>
                    <button onClick={() => changeLang('fr')} className={`lang-button ${lang === 'fr' ? 'active' : ''}`} aria-label="Français">
                        <span className="lang-flag">🇫🇷</span> FR
                    </button>
                    <button onClick={() => changeLang('it')} className={`lang-button ${lang === 'it' ? 'active' : ''}`} aria-label="Italiano">
                        <span className="lang-flag">🇮🇹</span> IT
                    </button>
                </div>
            </div>
            {/* Hero Section */}
            <header className="hero-section">
                <div className="section-container">
                    <div className="hero-content">
                        <div className="hero-text">

                            <h1 className="hero-title">
                                {t('heroTitle')}
                                <span className="highlight">{t('heroHighlight')}</span>
                            </h1>
                            <p className="hero-subtitle">
                                {t('heroSubtitle')}
                            </p>
                            <div className="hero-cta">
                                <button className="btn-primary" onClick={onNavigateToLogin}>
                                    {t('accessPlatform')}
                                </button>
                                <a href="/project-description" className="btn-secondary">{t('learnMore')}</a>
                                <button className="btn-secondary" onClick={() => setShowContactForm((v) => !v)}>
                                    {t('contactUs', 'Contact us')}
                                </button>
                            </div>

                            {showContactForm && (
                                <ContactForm />
                            )}
                        </div>
                        <div className="hero-visual">
                            <div className="desktop-mockup">
                               <img
                                    src={laptopImage}
                                    alt="Laptop mockup"
                                    className="laptop-image"
                                />  
                            </div>

                            <div className="phone-mockup">
                                <div className="phone-frame">
                                    <div className="phone-screen">
                                        <div className="phone-notch"></div>
                                        <div className="phone-content">
                                            <div className="phone-header">Matching Portal</div>
                                                            <div className="phone-deal-card deal-card-with-image deal-office">
                                                <div className="deal-overlay">
                                                    <div className="deal-badge">Real Estate</div>
                                                    <div className="deal-title">Commercial Property</div>
                                                    <div className="deal-location">Manhattan, NY</div>
                                                    <div className="deal-value">$12.5M</div>
                                                </div>
                                            </div>
                                            <div className="phone-deal-card">
                                                <div className="deal-badge secondary">Corporate</div>
                                                <div className="deal-title">M&A Opportunity</div>
                                                <div className="deal-location">Series B Round</div>
                                                <div className="deal-value">$5M</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* The Problem Section */}
            <section className="problem-section">
                <div className="section-container">
                    <h2 className="section-title">{t('whyChooseUs')}</h2>
                    <div className="problem-content">
                        <div className="problem-stat">
                            <div className="stat-number">Confidentiality</div>
                            <p>Bank-grade encryption and privacy controls ensure your sensitive deal information remains secure and protected from unauthorized access</p>
                        </div>
                        <div className="problem-stat">
                            <div className="stat-number">Speed</div>
                            <p>AI-powered matching connects you with qualified parties in minutes, not weeks, dramatically accelerating your deal cycle</p>
                        </div>
                        <div className="problem-stat">
                            <div className="stat-number">Transparency</div>
                            <p>Full visibility into deal flow, party credentials, and transaction history with immutable audit trails and real-time updates</p>
                        </div>
                        <div className="problem-stat">
                            <div className="stat-number">Technology</div>
                            <p>Advanced algorithms and institutional-grade infrastructure built for complex multi-party transactions and cross-sector matching</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <div className="section-container">
                    <h2 className="section-title">{t('intelligentMatching')}</h2>
                    <div className="features-grid">
                        {features.map((feature, index) => (
                            <div key={index} className="feature-card">
                                <div className="feature-number">{feature.number}</div>
                                <h3 className="feature-title">{feature.title}</h3>
                                <p className="feature-description">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="benefits-section">
                <div className="section-container">
                    <h2 className="section-title">{t('connectAcrossSectors')}</h2>
                    <div className="benefits-grid">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="benefit-card">
                                <div className="benefit-icon">{benefit.icon}</div>
                                <h3 className="benefit-title">{benefit.title}</h3>
                                <p className="benefit-description">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Comparison Section */}
            <section className="comparison-section">
                <div className="section-container">
                    <h2 className="section-title">Why Choose Our Platform</h2>
                    <div className="comparison-table">
                        <div className="comparison-row header-row">
                            <div className="comparison-cell feature-name">Feature</div>
                            <div className="comparison-cell our-platform">Our Platform</div>
                            <div className="comparison-cell traditional">Traditional Methods</div>
                        </div>
                        {[
                            { feature: 'Instant Deal Matching', ours: true, traditional: false },
                            { feature: 'Multi-Sector Coverage', ours: true, traditional: false },
                            { feature: 'Real-Time Network', ours: true, traditional: false },
                            { feature: '24/7 Access', ours: true, traditional: false },
                            { feature: 'AI-Powered Matching', ours: true, traditional: false },
                            { feature: 'Mobile Access', ours: true, traditional: false },
                        ].map((item, index) => (
                            <div key={index} className="comparison-row">
                                <div className="comparison-cell feature-name">{item.feature}</div>
                                <div className="comparison-cell our-platform">
                                    {item.ours ? '✓' : '✗'}
                                </div>
                                <div className="comparison-cell traditional">
                                    {item.traditional ? '✓' : '✗'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="section-container">
                    <h2 className="cta-title">{t('ctaTitle')}</h2>
                    <p className="cta-subtitle">{t('ctaSubtitle')}</p>
                    <button className="btn-primary btn-lg" onClick={onNavigateToLogin}>
                        {t('startMatching')}
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="section-container">
                    <div className="footer-content">
                        <div className="footer-section">
                            <h4>{t('platform')}</h4>
                            <ul>
                                <li><a href="#features">Features</a></li>
                                <li><a href="#about">About</a></li>
                                <li><a href="#contact">Contact</a></li>
                            </ul>
                        </div>
                        <div className="footer-section">
                            <h4>{t('legal')}</h4>
                            <ul>
                                <li><a href="#privacy">Privacy Policy</a></li>
                                <li><a href="#terms">Terms of Service</a></li>
                                <li><a href="#compliance">Compliance</a></li>
                            </ul>
                        </div>
<div className="footer-section">
                            <h4>{t('connect')}</h4>
    <ul>
        <li><a href="https://mamecapital.com/">XM Deals</a></li>
        <li><a href="https://www.linkedin.com/company/mamecapital/posts/?feedView=all">LinkedIn</a></li>
        <li><a href="mailto:info@xmdeals.com">Email</a></li>
    </ul>
</div>
                    </div>
                    <div className="footer-bottom">
                        <p>&copy; 2025 {t('footerCompany')}. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
